/**
 * @file: The right-click menu of the app.
 *
 * Two pages open a menu at the cursor. The calendar offers commands on an
 * interval. Compose offers the labels of a media as check items. The menu is
 * the same on both pages. The DOM, the position, the delegated listener and the
 * closing rules are therefore in this file. Each page gives only its entries.
 *
 * The page gives one `itemsAt` function. It gives back the entries for the
 * element under the cursor, or null to keep the menu of the browser. It can
 * also give back a promise, for a page that reads the entries from the server.
 *
 * One listener is delegated on the root of the page. A page rebuilds its rows
 * on every change, and a listener on a row would go away with it.
 *
 * The element is built one time and used again. It stands in the host, which is
 * outside the element that the page measures or rebuilds. The host is a
 * function, because a page builds its managers before Angular links the
 * template.
 *
 * The position is fixed. A page can put the menu over a scroll box, and a fixed
 * element is not clipped by one.
 *
 * The listeners run outside Angular. The page therefore gives `onAfterRun`,
 * which is normally a call to `$scope.$applyAsync`.
 */

/** Keep the menu away from the edges of the viewport. */
const EDGE_GAP = 4

/**
 * One entry of a menu.
 *
 * A command runs an action. A check item shows a state and turns it over. The
 * mark is the glyph or the colour before the label. A check item can have a
 * colour mark beside its check mark.
 *
 * @typedef {object} ContextMenuItem
 * @property {string} [kind] - 'command', the default, or 'check'
 * @property {string} label - The text of the entry
 * @property {Function} run - What the entry does
 * @property {boolean} [checked] - The state of a check item
 * @property {object} [mark] - `{ icon: 'fa-pen' }` or `{ colour: '#ff4136' }`
 */

class ContextMenu {
  /**
   * @param {object} options - The two things the menu needs from its page
   * @param {Function} options.host - Gives the element the menu is appended to
   * @param {Function} [options.onAfterRun] - Called after an entry has run
   */
  constructor ({ host, onAfterRun } = {}) {
    this.host = host || null
    this.element = null

    this._itemsAt = null
    this._root = null
    this._onAfterRun = onAfterRun || (() => {})
    this._onContextMenu = domEvent => this._handleContextMenu(domEvent)
    this._onOutsideDown = domEvent => {
      if (!this.element || !this.element.contains(domEvent.target)) {
        this.close()
      }
    }
    this._onKeyDown = domEvent => {
      if (domEvent.key === 'Escape') {
        this.close()
      }
    }
    // A menu of many entries scrolls itself, so a scroll in the menu is not a
    // reason to close it.
    this._onScroll = domEvent => {
      if (!this.element || !this.element.contains(domEvent.target)) {
        this.close()
      }
    }
  }

  /**
   * Listen for a right-click under one element.
   *
   * @param {Element} root - The element the listener sits on
   * @param {Function} itemsAt - Takes the target and the event. Gives the
   *   entries, a promise of the entries, or null to keep the browser menu
   */
  attach (root, itemsAt) {
    if (!root || this._root) {
      return
    }

    this._root = root
    this._itemsAt = itemsAt
    root.addEventListener('contextmenu', this._onContextMenu)
  }

  /** Give up the listener and the element. */
  detach () {
    if (this._root) {
      this._root.removeEventListener('contextmenu', this._onContextMenu)
      this._root = null
      this._itemsAt = null
    }

    this.destroy()
  }

  /**
   * Show the entries at a point of the viewport. `x` and `y` are clientX and
   * clientY of the event, which is the frame a fixed element uses.
   *
   * @param {number} x - The horizontal position in the viewport
   * @param {number} y - The vertical position in the viewport
   * @param {ContextMenuItem[]} items - The entries, in the order they are shown
   */
  show (x, y, items) {
    this.close()

    const element = this._ensureElement()

    if (!element) {
      return
    }

    items.forEach(item => element.appendChild(this._itemButton(item)))
    element.hidden = false
    this._place(x, y)

    document.addEventListener('mousedown', this._onOutsideDown, true)
    document.addEventListener('keydown', this._onKeyDown, true)
    // Use capture, so a scroll box in the page also closes the menu, like the window.
    window.addEventListener('scroll', this._onScroll, true)
  }

  /** Hide the menu and give up the listeners that close it. */
  close () {
    document.removeEventListener('mousedown', this._onOutsideDown, true)
    document.removeEventListener('keydown', this._onKeyDown, true)
    window.removeEventListener('scroll', this._onScroll, true)

    if (this.element) {
      this.element.hidden = true
      this.element.textContent = ''
    }
  }

  /** Close the menu and take the element out of the page. */
  destroy () {
    this.close()

    if (this.element && this.element.parentNode) {
      this.element.parentNode.removeChild(this.element)
    }

    this.element = null
  }

  // ===== Internals =====

  _handleContextMenu (domEvent) {
    if (!this._itemsAt || !domEvent.target || !domEvent.target.closest) {
      return
    }

    const answer = this._itemsAt(domEvent.target, domEvent)

    if (!answer) {
      return
    }

    if (typeof answer.then === 'function') {
      // The answer comes later, so the browser menu is refused now. The point
      // of the click is read now for the same reason.
      domEvent.preventDefault()
      this._showWhenReady(domEvent.clientX, domEvent.clientY, answer)
      return
    }

    if (answer.length === 0) {
      return
    }

    domEvent.preventDefault()
    this.show(domEvent.clientX, domEvent.clientY, answer)
  }

  _showWhenReady (x, y, answer) {
    return answer.then(items => {
      if (items && items.length > 0) {
        this.show(x, y, items)
      }
    })
  }

  /** The menu element. The code creates it in the host on first use. */
  _ensureElement () {
    if (this.element) {
      return this.element
    }

    const host = this.host && this.host()

    if (!host) {
      return null
    }

    this.element = document.createElement('div')
    // The floating class holds the position and the layer. A page that draws a
    // sample of the menu writes the first class only.
    this.element.className = 'jumbotron-menu jumbotron-menu-floating'
    this.element.setAttribute('role', 'menu')
    this.element.hidden = true
    // A second right-click on the menu must not open the browser menu over it.
    this.element.addEventListener('contextmenu', domEvent => domEvent.preventDefault())
    host.appendChild(this.element)

    return this.element
  }

  _itemButton (item) {
    const isCheck = item.kind === 'check'
    const button = document.createElement('button')

    button.type = 'button'
    button.className = 'jumbotron-menu-item'
    button.setAttribute('role', isCheck ? 'menuitemcheckbox' : 'menuitem')

    if (isCheck) {
      button.setAttribute('aria-checked', String(Boolean(item.checked)))
      button.appendChild(this._checkMark())
    }

    if (item.checked) {
      button.classList.add('is-checked')
    }

    const mark = this._markOf(item.mark)

    if (mark) {
      button.appendChild(mark)
    }

    button.appendChild(this._labelOf(item.label))
    button.addEventListener('click', () => {
      this.close()
      item.run()
      this._onAfterRun()
    })

    return button
  }

  /**
   * The check mark of a check item. It holds its place while the item is off,
   * so every label of the menu starts at the same left edge.
   */
  _checkMark () {
    const icon = document.createElement('i')

    icon.className = 'fas fa-check jumbotron-menu-check'
    icon.setAttribute('aria-hidden', 'true')

    return icon
  }

  /** The glyph or the colour before the label. Null when the entry has none. */
  _markOf (mark) {
    if (!mark) {
      return null
    }

    if (mark.colour) {
      const dot = document.createElement('span')
      // The colour is data, so the code sets it and the class does not.
      dot.className = 'jumbotron-dot'
      dot.style.backgroundColor = mark.colour

      return dot
    }

    if (!mark.icon) {
      return null
    }

    const icon = document.createElement('i')
    icon.className = `fas ${mark.icon}`
    icon.setAttribute('aria-hidden', 'true')

    return icon
  }

  _labelOf (text) {
    const label = document.createElement('span')

    label.className = 'jumbotron-menu-label'
    label.textContent = text

    return label
  }

  /**
   * Put the menu at the point of the cursor, inside the viewport.
   *
   * The code measures the menu after it adds the entries. The width follows the
   * longest label.
   */
  _place (x, y) {
    const element = this.element
    element.style.left = '0px'
    element.style.top = '0px'

    const box = element.getBoundingClientRect()
    const rightmost = Math.max(EDGE_GAP, window.innerWidth - box.width - EDGE_GAP)
    const lowest = Math.max(EDGE_GAP, window.innerHeight - box.height - EDGE_GAP)

    element.style.left = `${Math.min(Math.max(x, EDGE_GAP), rightmost)}px`
    element.style.top = `${Math.min(Math.max(y, EDGE_GAP), lowest)}px`
  }
}

export { ContextMenu }
